import React, { ReactElement, useEffect } from 'react';
import { useSafeArea } from 'react-native-safe-area-context';
import { useDispatch } from 'react-redux';

import { LoginFooter } from 'components/Footer';
import { ExclamationCircle } from 'components/icons';
import { CloseButton } from 'components/PopupModal/components/CloseButton';
import { useSafeParams } from 'components/Routes/useSafeParams';
import {
  Container,
  LimitWidth,
  NeedHelpLink,
  NeedHelpText,
} from 'components/Screens/LoginScreen/styled';
import { Redirect, useHistory } from 'cross-platform/react-router';
import { roles } from 'cross-platform/utils/roleProps';
import { useAccountCreationHandoff } from 'lib/api/hooks/useLogin/useAccountCreationHandoff';
import { openURL } from 'lib/navigation/openURL';
import { DataDog } from 'lib/reporter/utils/DataDog';
import { useQuery } from 'lib/router/useQuery';
import { Throbber } from 'sleepio/components/Throbber';
import { setIsHandoffAuthenticated } from 'state/user/actions';

import {
  EmptyBackground,
  ErrorIconContainer,
  FormattedText,
  TextContainer,
} from '../LoginCallbackScreen/styled';

const errorMessage =
  'We could not finish setting up your Sleepio account. For more information, visit:';

export const CreateAccountCallbackScreen = (): ReactElement => {
  const query = useQuery();
  const { productReference } = useSafeParams();
  const history = useHistory();
  const safeArea = useSafeArea();
  const dispatch = useDispatch();

  const status = query.get('status');
  const token = query.get('token');
  const hasValidParams = status === 'success' && Boolean(token);

  const { isAuthenticated, error } = useAccountCreationHandoff(
    hasValidParams ? token : null
  );

  useEffect(() => {
    if (!hasValidParams) {
      DataDog.addRumError('create_account_callback_invalid_params', '', {
        status: status ?? 'missing',
        hasToken: Boolean(token),
        tokenLength: token?.length ?? 0,
      });
    }
    // URL params are fixed at mount time — this should fire exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(setIsHandoffAuthenticated(true));
    }
  }, [isAuthenticated, dispatch]);

  const handleClose = () => {
    history.push(`/${productReference}/login`);
  };

  if (isAuthenticated) {
    return <Redirect to={`/${productReference}/home`} />;
  }

  const hasError = !hasValidParams || error;

  const loadingDisplay = (
    <EmptyBackground {...roles('CreateAccountCallbackScreen-loading')}>
      <Throbber {...roles('Throbber')} />
    </EmptyBackground>
  );

  const errorDisplay = (
    <Container {...roles('CreateAccountCallbackScreen-error')}>
      <CloseButton
        {...roles('close-button')}
        onPress={() => handleClose()}
        style={{
          position: 'absolute',
          top: safeArea.top + 10,
          right: safeArea.right + 24,
        }}
      />
      <ErrorIconContainer>
        <ExclamationCircle size={60} style={{ color: '#FD5A3A' }} />
      </ErrorIconContainer>
      <LimitWidth>
        <TextContainer {...roles('create-account-callback-error')}>
          <FormattedText>{errorMessage}</FormattedText>
          <FormattedText
            style={{
              paddingTop: 16,
            }}
          >
            sleepio.com/nhs
          </FormattedText>
        </TextContainer>
      </LimitWidth>
      <NeedHelpText {...roles('NeedHelpText')}>Need help? Contact</NeedHelpText>
      <NeedHelpLink
        onPress={() => openURL('mailto:hello@sleepio.com')}
        style={{ textAlign: 'center' }}
      >
        hello@sleepio.com
      </NeedHelpLink>
      <LoginFooter />
    </Container>
  );

  return <>{hasError ? errorDisplay : loadingDisplay}</>;
};

export default CreateAccountCallbackScreen;
